import Dialog from '@anm/components/modals/Dialog';
import AlertBlock from '@anm/components/notification/AlertBlock';
import isServer from '@anm/helpers/is/isServer';
import localStorage from '@anm/helpers/localStorage';
import useSwitchState from '@anm/hooks/useSwitchState';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import vtConfig from '../../config';
import { authSelectors } from '../../store/modules/auth';
import { userSelectors } from '../../store/modules/user';
import useProjectsEmptyCheck from '../OpenTwoOptionsDialog/hooks/useProjectsEmptyCheck';

import useCanOpenEditor from './hooks/useCanOpenEditor';
import useEditorUrl from './hooks/useEditorUrl';
import useUrl from './hooks/useUrl';

export type OpenEditorAction = 'edit' | 'create' | 'upload' | 'duplicate';

export type OpenEditorProps = {
  action: OpenEditorAction;
  children: (props: {
    editorUrl: string;
    href: string;
    canOpenEditor: boolean;
    handleClick: () => void;
  }) => JSX.Element;
  params?: EditorUrlParams;
  onlyStaff?: boolean;
  templateId?: string;
  isAuthControl?: boolean;
};

type EditorTarget = 'edit' | 'uploads' | 'stock-library';
export type EditorUrlParams = {
  embed?: boolean;
  format?: string;
  target?: EditorTarget;
  folderId?: number | null;
};

const addEditorUrlToLocalStore = (url: string) => {
  const key = 'signupRedirectUrl';
  localStorage().setItem(key, url);
};

const OpenEditor: FC<OpenEditorProps> = ({ params, action, onlyStaff, templateId, isAuthControl, children }) => {
  const [isModalOpened, openModal, closeModal] = useSwitchState();
  const editorUrl = useEditorUrl({ action, params, templateId });
  const url = useUrl({ editorUrl, isAuthControl });
  const isLoggedIn = useSelector(authSelectors.selectAuthLoginStatus);
  const isAuthPending = useSelector(authSelectors.selectAuthPending);
  const profile = useSelector(userSelectors.selectUserProfile);
  const canOpenEditor = useCanOpenEditor();

  const handleUpgradeClick = () => {
    closeModal();
    window.open(`${vtConfig().urls.wave}pricing`, '_blank');
  };

  const handleClick = () => {
    if (!canOpenEditor) {
      openModal();
      return;
    }

    !isLoggedIn && addEditorUrlToLocalStore(editorUrl);
  };

  useProjectsEmptyCheck();

  const canShow = !isServer() && !isAuthPending && (!onlyStaff || (onlyStaff && userSelectors.isUserStaff(profile)));
  if (!canShow) return null;

  return (
    <>
      {isModalOpened && (
        <Dialog onModalClose={closeModal} onSubmit={handleUpgradeClick}>
          <AlertBlock
            button={{
              text: 'Upgrade',
              size: 'medium',
              variant: 'yellow'
            }}
            onButtonClick={handleUpgradeClick}
          >
            <div>Please upgrade to create more projects</div>
          </AlertBlock>
        </Dialog>
      )}
      {children({ editorUrl, href: canOpenEditor ? url : '', canOpenEditor, handleClick })}
    </>
  );
};

export default OpenEditor;
